import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight, Instagram, Play, MessageCircle, Calendar,
  Video, ClipboardCheck, LineChart, PhoneCall, Dumbbell,
  Target, Users, ChevronLeft, ChevronRight, Plus, Minus,
} from "lucide-react";

/* =============================== CONFIG =============================== */
const WA_NUMBER = "34672480446"; // [NUMERO_WHATSAPP]
const WA_MSG = "Hola, he visto la web y quiero valorar qué preparación podría encajar con mi hijo. Tiene ___ años, juega de ___ y su principal objetivo es ___.";
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_MSG)}`;
const YT_ID = "uv1XHUgE6xk";
const YT_THUMB = `https://i.ytimg.com/vi/${YT_ID}/maxresdefault.jpg`;
const IG_LINK = "https://www.instagram.com/quiquevargas.coach/";

// Personal photos (childhood + current) — uploaded assets
// FOTO_INFANCIA: Abacena team photo (Quique as a kid) — we crop to his face
const FOTO_INFANCIA = "https://customer-assets-wrfwihn1.emergentagent.net/job_athletic-edge-19/artifacts/9rk41pfp_429dff70-b074-4dc9-9f2b-09ba4ffad58f.jpg";
const FOTO_ACTUAL   = "https://customer-assets-wrfwihn1.emergentagent.net/job_athletic-edge-19/artifacts/fycnb8ks_IMG_1625.webp";
const QV_LOGO       = "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/7jyn9i1w_IMG_2453.PNG";

// Testimonial photos (real players, matched by user description)
const KAI_IMG     = "https://customer-assets-wrfwihn1.emergentagent.net/job_athletic-edge-19/artifacts/rvh3j5ki_b7c29ee6-2914-482c-8f4b-484e32e8e77c%204.JPG"; // yellow + blue Mundoclima
const HUGO_IMG    = "https://customer-assets-wrfwihn1.emergentagent.net/job_athletic-edge-19/artifacts/z0xqjj0b_73123beb-180d-4fe4-bef6-e441a20556b1%206.webp"; // red night celebration hug
const SAMIR_IMG   = "https://customer-assets-wrfwihn1.emergentagent.net/job_athletic-edge-19/artifacts/3jmzrg4c_e4032c36-713e-4871-91cd-69905eb38e37%202.JPG"; // white jersey dribbling
const LUKA_IMG    = "https://customer-assets-wrfwihn1.emergentagent.net/job_athletic-edge-19/artifacts/ej68c9rh_70cf50fa-08ff-4e9d-ae0e-e0a534457b66%205.JPG"; // green jersey Puma
const RODRIGO_IMG = "https://customer-assets-wrfwihn1.emergentagent.net/job_athletic-edge-19/artifacts/sqatcwoi_c0faa238-ceea-4fac-8cf9-2ea095811bc0%203.jpg"; // blue+red AC Athletic Club dribbling

/* Track analytics events (window.gtag / window.dataLayer safe no-op) */
const track = (event, payload = {}) => {
  try {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...payload });
      if (typeof window.gtag === "function") window.gtag("event", event, payload);
    }
  } catch { /* noop */ }
};

const openWA = (source) => {
  track("whatsapp_click", { source });
  window.open(WA_LINK, "_blank", "noopener");
};

/* =============================== HOOKS =============================== */
const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, visible];
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity .8s cubic-bezier(.2,.7,.2,1) ${delay}ms, transform .8s cubic-bezier(.2,.7,.2,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

/* =============================== NAVBAR =============================== */
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on(); window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-[#050505]/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"}`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="brand-logo">
          <img src={QV_LOGO} alt="QV" className="h-9 w-9 sm:h-10 sm:w-10 object-contain" />
          <div className="leading-tight hidden sm:block">
            <div className="qv-display text-white text-lg">QUIQUE VARGAS</div>
            <div className="text-[10px] tracking-[0.3em] text-neutral-500 uppercase">Performance Coach</div>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.22em] text-neutral-300">
          <a href="#como-funciona" className="hover:text-white transition-colors" data-testid="nav-como">Cómo funciona</a>
          <a href="#mi-historia" className="hover:text-white transition-colors" data-testid="nav-historia">Mi historia</a>
          <a href="#programas" className="hover:text-white transition-colors" data-testid="nav-programas">Programas</a>
        </nav>
        <button
          onClick={() => openWA("nav")}
          className="qv-btn-primary text-xs"
          data-testid="nav-cta-whatsapp"
        >
          WHATSAPP <MessageCircle size={14} />
        </button>
      </div>
    </header>
  );
};

/* =============================== HERO =============================== */
const Hero = () => {
  const [playing, setPlaying] = useState(false);
  const onPlay = () => {
    track("video_hero_play", { video_id: YT_ID });
    setPlaying(true);
  };
  return (
    <section id="top" className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 overflow-hidden" data-testid="hero-section">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 text-center">
        <Reveal>
          <div className="text-[11px] sm:text-xs tracking-[0.28em] uppercase text-[#C1121F] font-bold mb-6">
            Preparación física para futbolistas de 12 a 18 años
          </div>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="qv-display text-white leading-[0.92] tracking-tight mx-auto max-w-[900px]"
              style={{ fontSize: "clamp(2.6rem, 6.5vw, 5.5rem)" }}
              data-testid="hero-headline">
            TU HIJO YA SE ESFUERZA.<br />
            ASEGURÉMONOS DE QUE ESE<br />
            ESFUERZO <span style={{ color: "#C1121F" }}>TENGA DIRECCIÓN</span>.
          </h1>
        </Reveal>
        <Reveal delay={240}>
          <p className="mt-8 sm:mt-10 text-neutral-300 text-base sm:text-lg leading-relaxed max-w-[640px] mx-auto">
            Un acompañamiento individual adaptado a sus entrenamientos, partidos y momento de desarrollo.
          </p>
        </Reveal>

        {/* Video block */}
        <Reveal delay={360}>
          <div className="mt-12 sm:mt-16">
            <div className="relative mx-auto rounded-2xl overflow-hidden border border-white/10 bg-black"
                 style={{ maxWidth: 1000, boxShadow: "0 30px 80px -30px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.03)" }}>
              <div className="absolute -top-px inset-x-8 h-[2px] bg-[#C1121F]/70" aria-hidden="true" />
              <div className="relative w-full" style={{ aspectRatio: "16/9" }}>
                {!playing ? (
                  <button
                    type="button"
                    onClick={onPlay}
                    className="group absolute inset-0 w-full h-full"
                    data-testid="hero-video-play"
                    aria-label="Reproducir vídeo"
                  >
                    <img src={YT_THUMB} alt="Vídeo Quique Vargas" className="w-full h-full object-cover"
                         onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${YT_ID}/hqdefault.jpg`; }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/30" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#C1121F] text-white shadow-[0_20px_60px_-10px_rgba(193,18,31,0.6)] transition-transform duration-300 group-hover:scale-105">
                        <Play size={32} fill="white" className="ml-1" />
                      </span>
                    </span>
                  </button>
                ) : (
                  <iframe
                    src={`https://www.youtube.com/embed/${YT_ID}?autoplay=1&rel=0&modestbranding=1`}
                    title="Quique Vargas — Preparación Física"
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={480}>
          <div className="mt-10 sm:mt-12">
            <button
              onClick={() => openWA("hero")}
              className="qv-btn-primary text-base"
              style={{ padding: "20px 40px" }}
              data-testid="hero-cta-whatsapp"
            >
              HABLAR POR WHATSAPP <MessageCircle size={20} />
            </button>
            <div className="mt-4 text-xs uppercase tracking-[0.22em] text-neutral-500">
              Sin compromiso · Te respondo personalmente
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* =============================== PROBLEMA =============================== */
const PAIN_ITEMS = [
  "Se lesiona o arrastra molestias con frecuencia.",
  "Su físico no evoluciona al ritmo que necesita.",
  "Entrena mucho, pero no sabes si entrena correctamente.",
  "Parece que su rendimiento se ha estancado.",
  "Te preocupa que se quede atrás.",
];

const Problem = () => (
  <section className="relative py-24 sm:py-32 bg-[#0D0D0F]" data-testid="problem-section">
    <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
      <Reveal>
        <h2 className="qv-display text-white leading-[0.95] max-w-3xl"
            style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
          CUANDO SABES QUE TU HIJO SE ESFUERZA,<br />
          <span style={{ color: "#C1121F" }}>PERO NO SABES SI ESTÁ AVANZANDO.</span>
        </h2>
      </Reveal>

      <div className="mt-14 sm:mt-20 space-y-6 sm:space-y-8">
        {PAIN_ITEMS.map((line, i) => (
          <Reveal key={i} delay={i * 120}>
            <div className="flex items-start gap-5 sm:gap-8 border-b border-white/5 pb-6 sm:pb-8" data-testid={`pain-${i}`}>
              <span className="qv-display text-[#C1121F]/60 text-2xl sm:text-3xl w-8 flex-shrink-0"
                    style={{ letterSpacing: "0.02em" }}>0{i + 1}</span>
              <p className="text-white text-lg sm:text-2xl leading-snug font-light">{line}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={200}>
        <p className="mt-14 sm:mt-16 text-neutral-400 text-base sm:text-lg leading-relaxed max-w-2xl">
          El problema no siempre es la falta de esfuerzo. Muchas veces es la falta de una <span className="text-white font-semibold">dirección individual</span>.
        </p>
      </Reveal>
    </div>
  </section>
);

/* =============================== SOLUCIÓN =============================== */
const SOLUCION_STEPS = ["PLANIFICA", "EJECUTA", "REVISA", "CORRIGE", "AJUSTA", "EVOLUCIONA"];

const Solution = () => (
  <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="solution-section">
    <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
      <div className="max-w-3xl">
        <Reveal>
          <h2 className="qv-display text-white leading-[0.95]"
              style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
            UN SISTEMA PROFESIONAL<br />
            <span style={{ color: "#C1121F" }}>ALREDEDOR DE SU FÚTBOL.</span>
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p className="mt-6 text-neutral-400 text-base sm:text-lg leading-relaxed">
            No se trata de añadir entrenamientos al azar. Se trata de organizar cada estímulo alrededor de su semana real.
          </p>
        </Reveal>
      </div>

      {/* Word marquee */}
      <div className="mt-16 sm:mt-20 overflow-hidden">
        <div className="qv-marquee-track qv-display text-white/8 whitespace-nowrap"
             style={{ fontSize: "clamp(4rem, 10vw, 8rem)", color: "rgba(255,255,255,0.05)" }}
             aria-hidden="true">
          {[...SOLUCION_STEPS, ...SOLUCION_STEPS].map((w, i) => (
            <span key={i} className="inline-block px-6">{w} <span style={{ color: "#C1121F" }}>·</span></span>
          ))}
        </div>
      </div>

      {/* Feature grid */}
      <div className="mt-16 sm:mt-20 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
        {[
          { icon: Calendar,  t: "Planificación semanal", d: "Cada sesión adaptada a su calendario de entrenamientos y partidos." },
          { icon: Video,     t: "Vídeos de ejercicios",  d: "Series, repeticiones, descansos y técnica correcta en la aplicación." },
          { icon: ClipboardCheck, t: "Registro completo", d: "Marca cada entrenamiento, RPE y cargas. Todo queda registrado." },
          { icon: LineChart, t: "Seguimiento del progreso", d: "Tests periódicos, fuerza, salto y velocidad medidos en el tiempo." },
          { icon: MessageCircle, t: "Corrección técnica", d: "Envía vídeos y recibe correcciones específicas de cada ejercicio." },
          { icon: PhoneCall, t: "Videollamadas cuando toca", d: "Contacto directo conmigo para hablar de su evolución y ajustes." },
        ].map((it, i) => {
          const Icon = it.icon;
          return (
            <Reveal key={i} delay={i * 80}>
              <div className="border border-white/6 bg-[#0D0D0F] p-7 sm:p-8 h-full transition-colors duration-300 hover:border-white/15" data-testid={`solution-feature-${i}`}>
                <Icon size={22} color="#C1121F" strokeWidth={2} />
                <h3 className="qv-display text-white text-2xl mt-6 leading-none">{it.t}</h3>
                <p className="mt-3 text-neutral-400 text-sm leading-relaxed">{it.d}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* =============================== CÓMO FUNCIONA =============================== */
const HowItWorks = () => (
  <section id="como-funciona" className="relative py-24 sm:py-32 bg-[#0D0D0F]" data-testid="how-section">
    <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
      <Reveal>
        <h2 className="qv-display text-white leading-[0.95] max-w-3xl"
            style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
          ONLINE NO SIGNIFICA<br />
          <span style={{ color: "#C1121F" }}>ENTRENAR SOLO.</span>
        </h2>
      </Reveal>

      <div className="mt-16 sm:mt-20 relative">
        {/* connector line */}
        <div className="hidden md:block absolute top-8 left-[8%] right-[8%] h-[1px] bg-gradient-to-r from-[#C1121F] via-[#C1121F]/40 to-[#C1121F]" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12 relative">
          {[
            { n: "01", t: "VALORACIÓN",   d: "Conozco su calendario, nivel, objetivos, posición y necesidades." },
            { n: "02", t: "PLANIFICACIÓN", d: "Recibe cada sesión en la aplicación con vídeos, cargas e indicaciones claras." },
            { n: "03", t: "SEGUIMIENTO", d: "Reviso su respuesta, corrijo sus vídeos y ajusto el programa cuando lo necesita." },
          ].map((s, i) => (
            <Reveal key={s.n} delay={i * 150}>
              <div className="text-left" data-testid={`how-step-${s.n}`}>
                <div className="w-16 h-16 flex items-center justify-center border border-[#C1121F] bg-[#0D0D0F] qv-display text-[#C1121F] text-2xl relative z-10 mx-auto md:mx-0">
                  {s.n}
                </div>
                <h3 className="qv-display text-white text-2xl sm:text-3xl mt-6 leading-none">{s.t}</h3>
                <p className="mt-4 text-neutral-400 text-sm sm:text-base leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal delay={200}>
        <p className="mt-16 text-center text-neutral-300 text-lg leading-relaxed max-w-2xl mx-auto">
          El acompañamiento continúa durante la semana, no termina al acabar una sesión presencial.
        </p>
      </Reveal>
    </div>
  </section>
);

/* =============================== MI HISTORIA =============================== */
const Story = () => (
  <section id="mi-historia" className="relative py-24 sm:py-32" data-testid="story-section">
    <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <Reveal>
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#C1121F] font-bold mb-6">Mi historia</div>
          <h2 className="qv-display text-white leading-[0.92]"
              style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
            ANTES DE PREPARAR FUTBOLISTAS,<br />
            FUI UN NIÑO QUE SOLO QUERÍA <span style={{ color: "#C1121F" }}>MEJORAR</span>.
          </h2>
        </Reveal>
        <div className="mt-8 space-y-5 text-neutral-300 text-base sm:text-lg leading-relaxed">
          <Reveal delay={100}><p>Juego al fútbol desde los 3 años y siempre he vivido este deporte con intensidad.</p></Reveal>
          <Reveal delay={200}><p>De pequeño tuve sobrepeso, y esa experiencia me hizo comprender lo importante que puede ser sentirse bien físicamente dentro y fuera del campo.</p></Reveal>
          <Reveal delay={300}><p>Me formé como <span className="text-white font-semibold">Técnico Superior en Acondicionamiento Físico</span> y obtuve la <span className="text-white font-semibold">certificación CPE</span>.</p></Reveal>
          <Reveal delay={400}><p>Hoy ayudo a jóvenes futbolistas a desarrollar las capacidades físicas que necesitan para competir mejor.</p></Reveal>
          <Reveal delay={500}><p className="text-white font-medium">Mi objetivo no es convertirlos en atletas alejados del balón. Es ayudarles a sacar su máximo potencial <span style={{ color: "#C1121F" }}>como futbolistas</span>.</p></Reveal>
        </div>
      </div>

      <div className="relative min-h-[560px]">
        <Reveal delay={100}>
          <div className="absolute top-0 left-0 w-[62%] aspect-[3/4] overflow-hidden border border-white/10 z-10 shadow-2xl">
            <img src={FOTO_INFANCIA} alt="Quique de pequeño" className="w-full h-full object-cover" style={{ objectPosition: "50% 30%" }} loading="lazy" />
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/70">Antes</div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={300}>
          <div className="absolute bottom-0 right-0 w-[62%] aspect-[3/4] overflow-hidden border border-[#C1121F]/40 shadow-2xl">
            <img src={FOTO_ACTUAL} alt="Quique Vargas hoy" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#C1121F]">Hoy</div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* =============================== AUTORIDAD =============================== */
const AUTH_ITEMS = [
  "DESDE LOS 3 AÑOS JUGANDO AL FÚTBOL",
  "TÉCNICO SUPERIOR EN ACONDICIONAMIENTO FÍSICO",
  "CERTIFICACIÓN CPE",
  "DECENAS DE FUTBOLISTAS ACOMPAÑADOS",
  "ESPECIALIZACIÓN EN PREPARACIÓN FÍSICA PARA FÚTBOL",
];

const Authority = () => (
  <section className="relative py-16 sm:py-20 bg-[#0D0D0F] border-y border-white/5 overflow-hidden" data-testid="authority-section">
    <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
      <div className="text-[10px] sm:text-xs tracking-[0.32em] uppercase text-[#C1121F] font-bold text-center mb-8">
        Experiencia aplicada al fútbol
      </div>
    </div>
    <div className="overflow-hidden">
      <div className="qv-marquee-track qv-display text-white whitespace-nowrap flex gap-16"
           style={{ fontSize: "clamp(1.2rem, 2.5vw, 2rem)" }} aria-hidden="true">
        {[...AUTH_ITEMS, ...AUTH_ITEMS, ...AUTH_ITEMS].map((it, i) => (
          <span key={i} className="inline-flex items-center gap-16 flex-shrink-0">
            {it}
            <span style={{ color: "#C1121F" }}>◆</span>
          </span>
        ))}
      </div>
    </div>
  </section>
);

/* =============================== TESTIMONIOS =============================== */
const TESTIMONIALS = [
  { img: HUGO_IMG,    name: "HUGO",    role: "Begur CF",         quote: "Llegué con problemas de rodilla y ahora soy titular. Me siento mejor que nunca y aguanto el partido a tope." },
  { img: KAI_IMG,     name: "KAI",     role: "Palamós CF",       quote: "Desde que entreno con Quique ya no se me cargan los gemelos. Soy pieza clave del equipo y este año he renovado." },
  { img: RODRIGO_IMG, name: "RODRIGO", role: "Gimnástica Segoviana", quote: "Estoy encantado. Ahora disfruto mucho más los entrenos y estoy mejorando fuerza y potencia para acceder a las pruebas." },
  { img: SAMIR_IMG,   name: "SAMIR",   role: "Futbolista amateur", quote: "Antes no sabía cómo entrenar en el gimnasio. Ahora tengo un plan claro y noto que rindo mejor los fines de semana." },
  { img: LUKA_IMG,    name: "LUKA",    role: "Juvenil academy",   quote: "El seguimiento por WhatsApp me da mucha tranquilidad. Sé que si tengo dudas o molestias Quique me responde el mismo día." },
];

const Testimonials = () => {
  const scroller = useRef(null);
  const scrollBy = (dir) => {
    if (!scroller.current) return;
    const w = scroller.current.clientWidth;
    scroller.current.scrollBy({ left: dir * w * 0.7, behavior: "smooth" });
  };
  return (
    <section className="relative py-24 sm:py-32" data-testid="testimonials-section">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <div className="flex items-end justify-between gap-6 mb-12">
          <Reveal>
            <h2 className="qv-display text-white leading-[0.95] max-w-2xl"
                style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
              LO IMPORTANTE NO ES SOLO EL PLAN.<br />
              <span style={{ color: "#C1121F" }}>ES LO QUE CAMBIA EN EL JUGADOR.</span>
            </h2>
          </Reveal>
          <div className="hidden md:flex items-center gap-2">
            <button onClick={() => scrollBy(-1)} className="w-11 h-11 border border-white/10 hover:border-white/30 flex items-center justify-center transition-colors" aria-label="Anterior" data-testid="testi-prev">
              <ChevronLeft size={18} />
            </button>
            <button onClick={() => scrollBy(1)} className="w-11 h-11 border border-white/10 hover:border-white/30 flex items-center justify-center transition-colors" aria-label="Siguiente" data-testid="testi-next">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div ref={scroller} className="flex gap-4 sm:gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-5 px-5 sm:mx-0 sm:px-0"
             style={{ scrollbarWidth: "none" }}>
          {TESTIMONIALS.map((t, i) => (
            <article key={i} className="flex-shrink-0 w-[85%] sm:w-[380px] snap-start border border-white/8 bg-[#0D0D0F] overflow-hidden" data-testid={`testimonial-${i}`}>
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={t.img} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black to-transparent" />
                <div className="absolute bottom-4 left-5 right-5">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#C1121F] font-bold">{t.role}</div>
                  <div className="qv-display text-white text-2xl mt-1 leading-none">{t.name}</div>
                </div>
              </div>
              <div className="p-6 sm:p-7">
                <p className="text-neutral-200 text-sm sm:text-base leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

/* =============================== PROGRAMAS =============================== */
const PROGRAMS = [
  { key: "start",       name: "START",       monthly: 49,  quarterly: null, tagline: "Para empezar a entrenar con una planificación profesional.", benefits: ["Aplicación profesional", "Planificación personalizada", "Vídeos de los ejercicios"], includes: ["Aplicación profesional", "Planificación personalizada", "Vídeos de los ejercicios", "Registro de entrenamientos", "Biblioteca de contenidos"] },
  { key: "essential",   name: "ESSENTIAL",   monthly: 97,  quarterly: 267,  tagline: "Para dejar de improvisar y empezar a progresar con seguimiento.", benefits: ["Ajustes quincenales", "Corrección técnica", "Soporte por WhatsApp"], includes: ["Todo lo de START", "Ajustes quincenales", "Corrección técnica", "Soporte por WhatsApp", "Seguimiento del progreso"] },
  { key: "performance", name: "PERFORMANCE", monthly: 147, quarterly: 397,  tagline: "El equilibrio entre planificación, seguimiento y ajustes frecuentes.", featured: true, badge: "MÁS RECOMENDADO", benefits: ["Ajustes semanales", "Videollamada mensual", "Seguimiento prioritario"], includes: ["Todo lo de ESSENTIAL", "Ajustes semanales", "Seguimiento prioritario", "Videollamada mensual", "Optimización de cargas", "Recuperación y hábitos"] },
  { key: "advanced",    name: "ADVANCED",    monthly: 197, quarterly: 537,  tagline: "Un salto de intensidad con trabajo preventivo específico.", benefits: ["Seguimiento muy cercano", "Trabajo preventivo", "Análisis físico"], includes: ["Todo lo de PERFORMANCE", "Seguimiento muy cercano", "Trabajo preventivo personalizado", "Análisis físico", "Ajustes prácticamente continuos", "Programación específica"] },
  { key: "elite",       name: "ELITE",       monthly: 297, quarterly: 797,  tagline: "Un acompañamiento muy cercano para entrenar como un profesional.", benefits: ["Contacto prioritario", "Análisis de vídeo", "Estrategia individual"], includes: ["Todo lo de ADVANCED", "Contacto prioritario", "Revisión constante", "Análisis de vídeo", "Estrategia individual", "Planificación completa"] },
  { key: "eliteplus",   name: "ELITE PLUS",  monthly: 497, quarterly: null, tagline: "Acompañamiento casi diario. Muy pocas plazas.", exclusive: true, badge: "ACOMPAÑAMIENTO PREMIUM", from: true, benefits: ["Seguimiento casi diario", "Videollamadas cuando sean necesarias", "Atención completamente personalizada"], includes: ["Seguimiento casi diario", "Videollamadas cuando sean necesarias", "Atención completamente personalizada", "Preparación física de máximo nivel", "Muy pocas plazas disponibles"] },
];

const Programs = () => {
  const [freq, setFreq] = useState("monthly");
  const [detail, setDetail] = useState(null);
  const scroller = useRef(null);

  useEffect(() => {
    // scroll to Performance card on mount
    if (scroller.current) {
      const card = scroller.current.querySelector('[data-featured="true"]');
      if (card) scroller.current.scrollLeft = Math.max(0, card.offsetLeft - 40);
    }
  }, []);

  const scrollBy = (dir) => {
    if (!scroller.current) return;
    const card = scroller.current.querySelector("article");
    const cardW = card ? card.offsetWidth + 20 : 400;
    scroller.current.scrollBy({ left: dir * cardW, behavior: "smooth" });
  };

  return (
    <section id="programas" className="relative py-24 sm:py-32 bg-[#0D0D0F]" data-testid="programs-section">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <h2 className="qv-display text-white leading-[0.95]"
                style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
              EL NIVEL DE ACOMPAÑAMIENTO<br />
              <span style={{ color: "#C1121F" }}>QUE NECESITA EN ESTE MOMENTO.</span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-6 text-neutral-400 text-base sm:text-lg leading-relaxed">
              No todos los futbolistas necesitan lo mismo. Por eso existen diferentes niveles de seguimiento.
            </p>
          </Reveal>
        </div>

        {/* Freq switcher */}
        <div className="mt-10 flex items-center justify-between gap-6 flex-wrap">
          <div className="inline-flex border border-white/10 p-1" role="tablist">
            {[
              { k: "monthly",   l: "MENSUAL" },
              { k: "quarterly", l: "TRIMESTRAL" },
            ].map((o) => (
              <button
                key={o.k}
                onClick={() => setFreq(o.k)}
                className={`px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold transition-colors ${freq === o.k ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
                data-testid={`freq-${o.k}`}
              >
                {o.l}
              </button>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button onClick={() => scrollBy(-1)} className="w-11 h-11 border border-white/10 hover:border-white/30 flex items-center justify-center transition-colors" aria-label="Anterior" data-testid="prog-prev">
              <ChevronLeft size={18} />
            </button>
            <button onClick={() => scrollBy(1)} className="w-11 h-11 border border-white/10 hover:border-white/30 flex items-center justify-center transition-colors" aria-label="Siguiente" data-testid="prog-next">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div ref={scroller} className="mt-8 flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-6 -mx-5 px-5 sm:mx-0 sm:px-0"
             style={{ scrollbarWidth: "none" }}>
          {PROGRAMS.map((p) => {
            const isQ = freq === "quarterly" && p.quarterly;
            const price = isQ ? p.quarterly : p.monthly;
            const unit = isQ ? "€/3 MESES" : "€/MES";
            const disabled = freq === "quarterly" && !p.quarterly;
            return (
              <article
                key={p.key}
                data-featured={p.featured ? "true" : "false"}
                data-testid={`program-${p.key}`}
                className={`flex-shrink-0 w-[85%] sm:w-[400px] snap-start relative flex flex-col ${p.featured ? "border-2 border-[#C1121F]" : p.exclusive ? "border border-[#C1121F]/40" : "border border-white/8"} bg-[#050505] p-7 sm:p-8 ${disabled ? "opacity-40" : ""}`}
                style={p.featured ? { boxShadow: "0 20px 60px -20px rgba(193,18,31,0.35)" } : {}}
              >
                {p.badge && (
                  <div className={`absolute -top-3 left-6 px-3 py-1 text-[10px] font-bold tracking-[0.24em] uppercase ${p.featured ? "bg-[#C1121F] text-white" : "bg-white text-black"}`}>
                    {p.badge}
                  </div>
                )}
                <div className="qv-display text-white text-3xl">{p.name}</div>
                <div className="mt-5 flex items-baseline gap-2">
                  {p.from && <span className="text-neutral-500 text-xs tracking-[0.2em] uppercase">Desde</span>}
                  <span className="qv-display text-white text-5xl leading-none">{price}</span>
                  <span className="text-neutral-500 text-[10px] tracking-[0.22em] uppercase">{unit}</span>
                </div>
                <p className="mt-4 text-neutral-400 text-sm leading-relaxed min-h-[48px]">{p.tagline}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-neutral-200">
                      <span className="mt-2 inline-block w-2 h-[2px] flex-shrink-0" style={{ background: "#C1121F" }} />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 space-y-3">
                  <button
                    onClick={() => openWA(`program_${p.key}`)}
                    disabled={disabled}
                    className={`w-full justify-center ${p.featured || p.exclusive ? "qv-btn-primary" : "qv-btn-ghost"} disabled:opacity-40 disabled:cursor-not-allowed`}
                    data-testid={`program-${p.key}-cta`}
                  >
                    CONSULTAR POR WHATSAPP <MessageCircle size={16} />
                  </button>
                  <button
                    onClick={() => { setDetail(p); track("program_details_open", { program: p.key }); }}
                    className="w-full text-center text-[11px] tracking-[0.24em] uppercase text-neutral-400 hover:text-white transition-colors py-2"
                    data-testid={`program-${p.key}-details`}
                  >
                    Ver qué incluye →
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {/* Details modal */}
      {detail && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setDetail(null)} role="dialog">
          <div className="bg-[#0D0D0F] border border-white/10 w-full sm:max-w-lg p-8 sm:p-10 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="program-detail-modal">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="qv-display text-white text-3xl">{detail.name}</div>
                {detail.badge && <div className="text-[10px] tracking-[0.24em] uppercase text-[#C1121F] font-bold mt-2">{detail.badge}</div>}
              </div>
              <button onClick={() => setDetail(null)} className="text-neutral-400 hover:text-white p-1" aria-label="Cerrar">
                <Plus size={22} style={{ transform: "rotate(45deg)" }} />
              </button>
            </div>
            <p className="text-neutral-300 text-sm leading-relaxed mb-6">{detail.tagline}</p>
            <ul className="space-y-3 mb-8">
              {detail.includes.map((it) => (
                <li key={it} className="flex items-start gap-3 text-sm text-neutral-200">
                  <span className="mt-2 inline-block w-2 h-[2px] flex-shrink-0" style={{ background: "#C1121F" }} />
                  {it}
                </li>
              ))}
            </ul>
            <button onClick={() => { openWA(`detail_${detail.key}`); setDetail(null); }} className="qv-btn-primary w-full justify-center">
              CONSULTAR POR WHATSAPP <MessageCircle size={18} />
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

/* =============================== FAQ =============================== */
const FAQ_ITEMS = [
  { q: "¿Cómo puede funcionar una preparación física online?", a: "El jugador recibe su planificación en la aplicación, envía vídeos cuando es necesario y el programa se ajusta según su evolución, calendario, fatiga y partidos." },
  { q: "¿Necesita entrenar en un gimnasio?", a: "No necesariamente. La planificación se adapta al material, espacio y circunstancias disponibles." },
  { q: "¿Se coordina con sus entrenamientos y partidos?", a: "Sí. El trabajo se organiza alrededor de la carga que ya recibe con su equipo." },
  { q: "¿Cómo sé qué programa necesita mi hijo?", a: "No tienes que decidirlo solo. Cuéntame su situación por WhatsApp y te explicaré qué nivel de acompañamiento puede encajar mejor." },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="relative py-24 sm:py-32" data-testid="faq-section">
      <div className="max-w-[900px] mx-auto px-5 sm:px-8">
        <Reveal>
          <h2 className="qv-display text-white leading-[0.95]" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
            DUDAS <span style={{ color: "#C1121F" }}>RÁPIDAS.</span>
          </h2>
        </Reveal>
        <div className="mt-12 divide-y divide-white/8 border-y border-white/8">
          {FAQ_ITEMS.map((it, i) => {
            const isOpen = open === i;
            return (
              <div key={i} data-testid={`faq-${i}`}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-6 text-left group">
                  <span className="qv-display text-white text-lg sm:text-xl leading-tight group-hover:text-[#C1121F] transition-colors">{it.q}</span>
                  <span className="flex-shrink-0 w-9 h-9 flex items-center justify-center border border-white/15">
                    {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                  </span>
                </button>
                <div
                  className="overflow-hidden transition-all duration-400 ease-out"
                  style={{ maxHeight: isOpen ? 240 : 0, opacity: isOpen ? 1 : 0 }}
                >
                  <p className="pb-6 text-neutral-400 text-base leading-relaxed pr-12">{it.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* =============================== CTA FINAL =============================== */
const FinalCTA = () => (
  <section className="relative py-32 sm:py-40 bg-[#050505] overflow-hidden" data-testid="final-cta-section">
    <div className="absolute inset-0 pointer-events-none opacity-30">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px]"
           style={{ background: "radial-gradient(ellipse at center, rgba(193,18,31,0.35) 0%, transparent 60%)", filter: "blur(60px)" }} />
    </div>
    <div className="relative max-w-[900px] mx-auto px-5 sm:px-8 text-center">
      <Reveal>
        <h2 className="qv-display text-white leading-[0.95]"
            style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
          TU HIJO YA PONE EL ESFUERZO.<br />
          <span style={{ color: "#C1121F" }}>AYUDÉMOSLE A PONERLE DIRECCIÓN.</span>
        </h2>
      </Reveal>
      <Reveal delay={120}>
        <p className="mt-8 text-neutral-300 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
          Cuéntame su edad, posición, nivel y principal objetivo. Te responderé personalmente y valoraremos qué puede necesitar.
        </p>
      </Reveal>
      <Reveal delay={240}>
        <div className="mt-12">
          <button onClick={() => openWA("final")} className="qv-btn-primary text-base"
                  style={{ padding: "22px 44px" }} data-testid="final-cta-whatsapp">
            HABLAR POR WHATSAPP <MessageCircle size={20} />
          </button>
          <div className="mt-4 text-xs uppercase tracking-[0.24em] text-neutral-500">
            Sin compromiso · Atención personal
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* =============================== FOOTER =============================== */
const Footer = () => (
  <footer className="border-t border-white/8 py-12 pb-24 md:pb-12" data-testid="site-footer">
    <div className="max-w-[1200px] mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <img src={QV_LOGO} alt="QV" className="h-10 w-10 object-contain" />
        <div className="leading-tight">
          <div className="qv-display text-white text-base">QUIQUE VARGAS</div>
          <div className="text-[10px] tracking-[0.32em] text-neutral-500 uppercase">Performance Coach</div>
        </div>
      </div>
      <div className="flex items-center gap-6 text-neutral-400 text-sm">
        <a href={IG_LINK} target="_blank" rel="noreferrer" className="hover:text-white transition-colors inline-flex items-center gap-2">
          <Instagram size={16} /> @quiquevargas.coach
        </a>
        <button onClick={() => openWA("footer")} className="hover:text-white transition-colors inline-flex items-center gap-2">
          <MessageCircle size={16} /> WhatsApp
        </button>
      </div>
      <div className="text-[10px] tracking-[0.28em] uppercase text-neutral-600">© 2026 Quique Vargas</div>
    </div>
  </footer>
);

/* =============================== MOBILE STICKY CTA =============================== */
const MobileCTA = () => (
  <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-[#050505]/95 backdrop-blur-xl border-t border-white/10 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]" data-testid="mobile-cta-bar">
    <button onClick={() => openWA("mobile_sticky")} className="qv-btn-primary w-full justify-center" data-testid="mobile-cta-whatsapp">
      HABLAR POR WHATSAPP <MessageCircle size={18} />
    </button>
  </div>
);

/* =============================== EXPORT =============================== */
export default function Landing() {
  useEffect(() => {
    document.title = "Preparación física online para futbolistas jóvenes | Quique Vargas";
    const meta = document.querySelector('meta[name="description"]') || document.createElement("meta");
    meta.name = "description";
    meta.content = "Preparación física individual para futbolistas de 12 a 18 años, adaptada a sus entrenamientos, partidos y evolución. Habla conmigo por WhatsApp.";
    if (!meta.parentNode) document.head.appendChild(meta);
  }, []);

  return (
    <div className="bg-[#050505] text-white min-h-screen">
      <Navbar />
      <Hero />
      <Problem />
      <Solution />
      <HowItWorks />
      <Story />
      <Authority />
      <Testimonials />
      <Programs />
      <FAQ />
      <FinalCTA />
      <Footer />
      <MobileCTA />
    </div>
  );
}
